import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalService } from '../../../../shared/services/modal.service';
import { AccountAggregatorService } from '../../../../core/services/account-aggregator.service';
import { AccountData, AATransaction } from '../../../../shared/models/account-aggregator.interface';


import { OverlayModule } from '@angular/cdk/overlay';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';
import { FiDataService } from '../../../../core/services/fi-data.service';

interface ExpenseDay {
  day: string;
  value: number;
  isHighlighted?: boolean;
}

@Component({
  selector: 'app-expense-total-widget',
  standalone: true,
  imports: [CommonModule, WidgetEdit, OverlayModule],
  templateUrl: './expense-total-widget.html',
  styleUrl: './expense-total-widget.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExpenseTotalWidgetComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('smallCanvas') smallCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('mediumCanvas') mediumCanvasRef!: ElementRef<HTMLCanvasElement>;

  private resizeObserver: ResizeObserver | null = null;
  private rafId: number = 0;

  @Input() expenseData: ExpenseDay[] = [];
  @Input() isEditMode: boolean = false;
  @Input() isAddMode: boolean = false;
  @Output() addWidget = new EventEmitter<void>();
  @Input() size: 'small' | 'medium' | 'half' | 'full' = 'small';
  @Output() editCall = new EventEmitter<any>();
  @Input() index = 0;
  @Input() activeDropdownIndex: number | null = null;
  @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
  @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
  @Input() changeWidgetSize!: (index: number, size: any) => void;
  @Input() removeWidget!: (index: number) => void;
  @Input() title = 'Expense Total';
  @Input() startDate?: string; // Date range from API (e.g., "2025-01-21")
  @Input() endDate?: string; // Date range from API (e.g., "2026-01-27")

  maxValue: number = 100;
  hoveredData: ExpenseDay | null = null;
  tooltipVisible: boolean = false;
  tooltipPosition = { x: 0, y: 0 };
  private barPositions: Array<{ x: number; y: number; width: number; height: number; data: ExpenseDay }> = [];
  topExpense: ExpenseDay | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private elementRef: ElementRef,
    private accountAggregatorService: AccountAggregatorService,
    private fiDataService: FiDataService
  ) { }

  accountsData: AccountData[] = [];
  accounts: AccountData[] = [];
  selectedAccountIds: string[] = []; // Track selected account IDs from modal
  private modalSubscription: any;

  ngOnInit() {
    this.maxValue = Math.max(...this.expenseData.map(d => d.value));
    // Find the expense with the highest value
    this.topExpense = this.expenseData.reduce((max, current) =>
      current.value > max.value ? current : max
      , this.expenseData[0]);

    this.loadAccountData();

    // Subscribe to account selection changes from modal
    this.modalSubscription = this.modalService.expenseAccountSelection$.subscribe(selectedAccounts => {

      if (selectedAccounts && selectedAccounts.length > 0) {
        this.onAccountSelectionChange(selectedAccounts);
      }
    });
  }

  /**
   * Get the year from the API date range
   * Extracts year from startDate if available, otherwise uses current year
   */
  private getYearFromDateRange(): number {
    if (this.startDate) {
      const date = new Date(this.startDate);
      return date.getFullYear();
    }
    return new Date().getFullYear();
  }

  /**
   * Calculate monthly expense data from Account Aggregator API transactions
   * Year is automatically extracted from startDate or current year if not provided
   * @param accounts Array of account data from the API
   * @param year Optional target year (if not provided, uses year from startDate or current year)
   * @returns Array of ExpenseDay objects with monthly totals
   */
  calculateMonthlyExpenses(accounts: AccountData[], year?: number): ExpenseDay[] {
    // Filter accounts based on selection (if any selections made)
    let filteredAccounts = accounts;
    if (this.selectedAccountIds.length > 0) {
      filteredAccounts = accounts.filter(account => {
        // Extract last 4 digits from maskedAccountNumber to compare with cardNumber
        const last4Digits = account.maskedAccountNumber?.slice(-4) || '';
        return this.selectedAccountIds.includes(last4Digits);
      });
    }
    // If no accounts selected or filtered list is empty, use all accounts
    if (filteredAccounts.length === 0) {
      filteredAccounts = accounts;
    }
    // Get all transactions from filtered accounts
    const allTransactions = this.accountAggregatorService.getAllTransactions(filteredAccounts);


    // Determine the end date (use endDate from API or current date)
    const endDate = this.endDate ? new Date(this.endDate) : new Date();

    // Calculate start date as 6 months before end date
    const startDate = new Date(endDate);
    startDate.setMonth(startDate.getMonth() - 5); // Go back 5 months (current month + 5 = 6 months)
    startDate.setDate(1); // First day of that month

    // Filter transactions for the last 6 months and DEBIT type only
    const filteredTransactions = allTransactions.filter(transaction => {
      const transactionDate = new Date(transaction.transactionTimestamp);
      return transactionDate >= startDate &&
        transactionDate <= endDate &&
        transaction.type === 'DEBIT';
    });



    // Generate month labels for the last 6 months
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const monthlyData: { month: string; year: number; total: number }[] = [];

    // Generate 6 months starting from 5 months ago to current month
    for (let i = 0; i < 6; i++) {
      // Create a fresh date object for each iteration to avoid mutation issues
      const monthDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      monthDate.setMonth(monthDate.getMonth() - (5 - i)); // Start from 5 months ago

      const month = monthNames[monthDate.getMonth()];
      const year = monthDate.getFullYear();

      monthlyData.push({
        month: month,
        year: year,
        total: 0
      });
    }

    // Calculate total debit amount for each month
    filteredTransactions.forEach(transaction => {
      const transactionDate = new Date(transaction.transactionTimestamp);
      const txnMonth = transactionDate.getMonth();
      const txnYear = transactionDate.getFullYear();

      // Find the matching month in our monthlyData
      const monthIndex = monthlyData.findIndex(m =>
        m.month === monthNames[txnMonth] && m.year === txnYear
      );

      if (monthIndex !== -1) {
        const amount = parseFloat(transaction.amount || '0');
        monthlyData[monthIndex].total += amount;
      }
    });

    // Find the maximum expense value
    const maxExpense = Math.max(...monthlyData.map(m => m.total));

    // Transform to ExpenseDay format
    return monthlyData.map((data) => ({
      day: data.month,
      value: data.total,
      isHighlighted: data.total === maxExpense && maxExpense > 0 // Highlight month with max expense
    }));
  }

  /**
   * Update expense data from API accounts
   * Year is automatically extracted from the startDate input property
   * @param accounts Array of account data from the API
   */
  updateExpenseDataFromAPI(accounts: AccountData[]): void {
    this.expenseData = this.calculateMonthlyExpenses(accounts);
    this.maxValue = Math.max(...this.expenseData.map(d => d.value));
    this.topExpense = this.expenseData.reduce((max, current) =>
      current.value > max.value ? current : max
      , this.expenseData[0]);

    // Use RAF to sync chart redraw with paint cycle
    cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.drawCharts();
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit() {
    this.setupResizeObserver();
    this.rafId = requestAnimationFrame(() => {
      this.drawCharts();
    });
  }

  private resizeDebounce: any = null;

  private setupResizeObserver() {
    // Find the parent article.widget element
    const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;

    if (!widgetElement) {
      if (!this.isAddMode) {
        //console.warn('Could not find parent widget element');
      }
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.resizeDebounce);
      this.resizeDebounce = setTimeout(() => {
        const isDragging = document.body.classList.contains('is-resizing');
        const resizingWidgetType = document.body.dataset['resizingWidget'];

        // Update if:
        // 1. Not dragging (browser resize, toggle, initial load)
        // 2. Dragging THIS widget specifically
        if (!isDragging || resizingWidgetType === 'expensetotal') {
          this.drawCharts();
          this.cdr.markForCheck();
        }
      }, 16); // ~1 frame debounce
    });
    this.resizeObserver.observe(widgetElement);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['size'] && !changes['size'].firstChange) {
      cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => {
        this.drawCharts();
      });
    }
  }

  ngOnDestroy() {
    clearTimeout(this.resizeDebounce);
    cancelAnimationFrame(this.rafId);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.modalSubscription) {
      this.modalSubscription.unsubscribe();
    }
  }

  private drawCharts() {
    if (this.size === 'small' && this.smallCanvasRef) {
      this.drawSmallChart();
    } else if (this.size !== 'small' && this.mediumCanvasRef) {
      this.drawMediumChart();
    }
  }

  private drawSmallChart() {
    const canvas = this.smallCanvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with device pixel ratio for sharp rendering
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;

    // Get container having class widget height for dynamic canvas height
    const widgetContainer = this.elementRef.nativeElement;
    const articleWidget = widgetContainer.closest('article.widget') as HTMLElement;
    const containerHeight = articleWidget ? articleWidget.offsetHeight : 231;

    const canvasHeightRatio = 0.5844; // 132/227 proportion
    const calculatedCanvasHeight = containerHeight * canvasHeightRatio;

    canvas.height = calculatedCanvasHeight * dpr;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, calculatedCanvasHeight);

    // Clear bar positions
    this.barPositions = [];

    // Configuration - reduced padding for more vertical space
    const padding = { left: 50, right: 20, top: 8, bottom: 8 };
    const chartWidth = rect.width - padding.left - padding.right;
    const chartHeight = calculatedCanvasHeight - padding.top - padding.bottom;
    const barHeight = 8.5;
    const displayData = this.expenseData; // Show all 6 months
    const numBars = displayData.length;
    const totalBarHeight = numBars * barHeight;
    const totalSpacing = chartHeight - totalBarHeight;
    const barSpacing = Math.max(3, totalSpacing / (numBars + 1));

    // Draw horizontal bars and labels
    // Helper function for logarithmic scale
    const getLogScaleSmall = (value: number, maxVal: number): number => {
      if (value === 0) return 0;
      const logValue = Math.log10(value + 1);
      const logMax = Math.log10(maxVal + 1);
      return logValue / logMax;
    };

    displayData.forEach((data, index) => {
      const y = padding.top + barSpacing + (index * (barHeight + barSpacing));

      // Use logarithmic scale for bar width
      const scaleFactor = getLogScaleSmall(data.value, this.maxValue);
      const barWidth = scaleFactor * chartWidth;

      // Draw label on the left with fallback font
      ctx.fillStyle = getComputedStyle(document.body).getPropertyValue('--color-gray') || 'rgba(99, 104, 111, 0.8)';
      ctx.font = 'bold 11px Switzer, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(data.day, padding.left - 10, y + barHeight / 2);

      // Draw bar with rounded corners
      const barColor = data.isHighlighted ? (getComputedStyle(document.body).getPropertyValue('--color-orange') || '#E7733A') : (getComputedStyle(document.body).getPropertyValue('--color-graylight') || 'rgba(158, 164, 172, 0.8)');
      ctx.fillStyle = barColor;
      this.roundRect(ctx, padding.left, y, Math.max(barWidth, 8), barHeight, 4);

      // Store bar position for hover detection
      this.barPositions.push({
        x: padding.left,
        y: y,
        width: Math.max(barWidth, 8),
        height: barHeight,
        data: data
      });
    });
  }

  private drawMediumChart() {
    const canvas = this.mediumCanvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with device pixel ratio for sharp rendering
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;

    // Get container having class widget height on resize
    const widgetContainer = this.elementRef.nativeElement;
    const articleWidget = widgetContainer.closest('article.widget') as HTMLElement;
    const containerHeight = articleWidget ? articleWidget.offsetHeight : 231;

    const canvasHeightRatio = 0.5844; // 132/227 proportion
    const calculatedCanvasHeight = containerHeight * canvasHeightRatio;


    canvas.height = calculatedCanvasHeight * dpr;

    ctx.scale(dpr, dpr);

    // Clear canvas
    //ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.clearRect(0, 0, rect.width, calculatedCanvasHeight);

    // Clear bar positions
    this.barPositions = [];

    // Configuration
    const padding = { left: 8, right: 8, top: 15, bottom: 28 };
    const chartWidth = rect.width - padding.left - padding.right;
    //const chartHeight = rect.height - padding.top - padding.bottom;
    const chartHeight = calculatedCanvasHeight - padding.top - padding.bottom;

    // Use all expense data (6 months)
    let displayData = this.expenseData;
    /*if (this.size === 'medium') {
      displayData = this.expenseData.slice(0, 7);
    }*/
    const numBars = displayData.length;
    // Calculate bar width to ensure all bars fit with proper spacing
    const barWidth = Math.min(32, Math.floor(chartWidth / (numBars + (numBars + 1) * 0.3)));
    const totalBarWidth = numBars * barWidth;
    const totalSpacing = chartWidth - totalBarWidth;
    const barSpacing = totalSpacing / (numBars + 1);

    // Determine label skip based on widget container width (not canvas width)
    const widgetWidth = articleWidget ? articleWidget.offsetWidth : rect.width;
    let labelSkip = 1; // Show all labels by default
    let fontSize = 12;

    if (widgetWidth < 220) {
      labelSkip = 4; // Show every 4th label
      fontSize = 8;
    } else if (widgetWidth < 300) {
      labelSkip = 3; // Show every 3rd label
      fontSize = 9;
    } else if (widgetWidth < 350) {
      labelSkip = 2; // Show every 2nd label
      fontSize = 10;
    }

    // Draw vertical bars and labels


    // Helper function: logarithmic scale for better visualization of extreme outliers
    const getLogScale = (value: number, maxVal: number): number => {
      if (value === 0) return 0;
      const logValue = Math.log10(value + 1);
      const logMax = Math.log10(maxVal + 1);
      return logValue / logMax;
    };

    displayData.forEach((data, index) => {
      const x = padding.left + barSpacing + (index * (barWidth + barSpacing));

      // Use logarithmic scale for better visualization
      const scaleFactor = getLogScale(data.value, this.maxValue);
      const barHeight = scaleFactor * chartHeight;

      const y = padding.top + chartHeight - barHeight;

      // Draw bar with rounded corners
      const barColor = data.isHighlighted ? (getComputedStyle(document.body).getPropertyValue('--color-orange') || '#E7733A') : (getComputedStyle(document.body).getPropertyValue('--color-graylight') || 'rgba(158, 164, 172, 0.80)');
      ctx.fillStyle = barColor;
      const radius = barHeight < 10 ? 0 : 8;
      this.roundRect(ctx, x, y, barWidth, Math.max(barHeight, 3), radius);

      // Draw label at the bottom - only draw labels based on skip value
      if (index % labelSkip === 0) {
        ctx.fillStyle = getComputedStyle(document.body).getPropertyValue('--color-gray') || 'rgba(99, 104, 111, 0.8)';
        ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        //const labelY = rect.height - 20;
        const labelY = calculatedCanvasHeight - 20;
        ctx.fillText(data.day, x + barWidth / 2, labelY);
      }

      // Store bar position for hover detection
      this.barPositions.push({
        x: x,
        y: y,
        width: barWidth,
        height: Math.max(barHeight, 3),
        data: data
      });
    });
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.fill();
  }

  onCanvasHover(event: MouseEvent, chartType: 'small' | 'medium'): void {
    const canvas = chartType === 'small' ? this.smallCanvasRef?.nativeElement : this.mediumCanvasRef?.nativeElement;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    // Check if mouse is over any bar
    const hoveredBar = this.barPositions.find(bar => {
      return mouseX >= bar.x && mouseX <= bar.x + bar.width &&
        mouseY >= bar.y && mouseY <= bar.y + bar.height;
    });

    if (hoveredBar) {
      this.hoveredData = hoveredBar.data;
      this.tooltipVisible = true;

      // Position tooltip
      if (chartType === 'small') {
        // For horizontal bars, position tooltip to the right of the bar
        this.tooltipPosition = {
          x: Math.min(hoveredBar.x + hoveredBar.width + 10, rect.width - 120),
          y: hoveredBar.y + hoveredBar.height / 2 - 20
        };
      } else {
        // For vertical bars, position tooltip above the bar
        // Calculate centered position
        let tooltipX = hoveredBar.x + hoveredBar.width / 2 - 60;

        // Ensure tooltip doesn't go off the left edge (minimum 5px padding)
        tooltipX = Math.max(tooltipX, 5);

        // Ensure tooltip doesn't go off the right edge (120px is approximate tooltip width)
        tooltipX = Math.min(tooltipX, rect.width - 125);

        this.tooltipPosition = {
          x: tooltipX,
          y: Math.max(hoveredBar.y - 50, 0)
        };
      }
    } else {
      this.hideTooltip();
    }
  }

  hideTooltip(): void {
    this.tooltipVisible = false;
    this.hoveredData = null;
  }

  openModal(): void {
    //return if edit mode or add mode is on
    if (this.isEditMode || this.isAddMode) return;
    if (this.accountsData && this.accountsData.length > 0) {
      const modalData = this.accountAggregatorService.transformAccountsForModal(this.accountsData);
      this.modalService.openExpenseModal(modalData.accounts, modalData.creditCards);
    } else {
      this.modalService.openExpenseModal();
    }
  }

  /**
   * Handle account selection changes from modal
   * @param selectedAccounts Array of selected account items from modal
   */
  onAccountSelectionChange(selectedAccounts: any[]): void {
    // Extract account IDs from selected items
    this.selectedAccountIds = selectedAccounts.map(item => item.cardNumber);

    // Recalculate expenses with filtered accounts
    if (this.accountsData && this.accountsData.length > 0) {
      this.updateExpenseDataFromAPI(this.accountsData);
    }
  }
  private loadAccountData(): void {
    // TODO: Replace with actual consentID from user session
    let consentID: any = localStorage.getItem('active_consent_id');; // This should come from user authentication/session

    this.fiDataService.getAllFIData(consentID, 'cache').subscribe({
      next: (response) => {
        this.accountsData = this.accountAggregatorService.parseAccountAggregatorResponse(response);
        //this.accounts = this.accountAggregatorService.transformAccountsForSummaryWidget(this.accountsData);

        // Pass accountsData (raw parsed data with transactions), not transformed accounts
        this.updateExpenseDataFromAPI(this.accountsData);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading account data:', error);
      }
    });
  }
}
